import { FC, useMemo } from 'react'
import {
  Wrapper,
  SectionTitle,
  SectionRow,
  RowTitle,
  RowValue,
  Divider,
} from './PlanHistory.styles'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  selectForecastItems,
  selectPlannedItems,
  selectShipmentItems,
  selectSubmittedItems,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { useSelector } from 'react-redux'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import { getPlannedShippedForecastForMonth } from '@/core/utils/getPlannedShippedForecastForMonth.ts'
import { formatNumber } from '@/core/utils/formatNumber'

interface Props {
  date: Date
  product: Product
  color?: 'primary' | 'secondary'
  hideActual?: boolean
}

const PlanHistory: FC<Props> = ({
  date,
  product,
  color = 'primary',
  hideActual,
}) => {
  const forecastItems = useSelector(selectForecastItems)
  const plannedItems = useSelector(selectPlannedItems)
  const shipmentItems = useSelector(selectShipmentItems)
  const submittedItems = useSelector(selectSubmittedItems)

  const { t } = useTranslation()
  const historyDateFormat = t('planHistory.dateFormat')

  const { planned, shipped, submitted, history } = useMemo(() => {
    const {
      plannedAmount,
      plannedCost,
      shippedAmount,
      shippedCost,
      submittedAmount,
      submittedCost,
    } = getPlannedShippedForecastForMonth(product, date, {
      forecast: forecastItems,
      planned: plannedItems,
      shipments: shipmentItems,
      submitted: submittedItems,
    })

    let plannedValue = plannedAmount != null ? formatNumber(plannedAmount) : '—'
    let shippedValue = shippedAmount ? formatNumber(shippedAmount) : '—'
    let submittedValue = submittedAmount ? formatNumber(submittedAmount) : '—'

    if (plannedCost != null) {
      plannedValue += ` (${formatCurrency(plannedCost, { maximumFractionDigits: 0 })})`
    }

    if (shippedCost) {
      shippedValue += ` (${formatCurrency(shippedCost, { maximumFractionDigits: 0 })})`
    }

    if (submittedCost) {
      submittedValue += ` (${formatCurrency(submittedCost, { maximumFractionDigits: 0 })})`
    }

    const previousMonthInPreviousYear = dayjs(date)
      .subtract(1, 'month')
      .subtract(1, 'year')
    const currentMonthInPreviousYear = dayjs(date).subtract(1, 'year')
    const nextMonthInPreviousYear = dayjs(date)
      .add(1, 'month')
      .subtract(1, 'year')

    const history: Record<string, string>[] = [
      previousMonthInPreviousYear,
      currentMonthInPreviousYear,
      nextMonthInPreviousYear,
    ].map((date) => {
      const { shippedAmount } = getPlannedShippedForecastForMonth(
        product,
        date.toDate(),
        {
          forecast: forecastItems,
          planned: plannedItems,
          shipments: shipmentItems,
          submitted: submittedItems,
        },
      )

      return {
        month: date.format(historyDateFormat),
        shipped: formatNumber(shippedAmount ?? 0),
      }
    })

    return {
      planned: plannedValue,
      shipped: shippedValue,
      submitted: submittedValue,
      history: history,
    }
  }, [
    date,
    forecastItems,
    historyDateFormat,
    plannedItems,
    product,
    shipmentItems,
    submittedItems,
  ])

  return (
    <Wrapper color={color} elevation={0}>
      <SectionTitle>{dayjs(date).format(historyDateFormat)}</SectionTitle>
      {!hideActual && (
        <>
          <SectionRow>
            <RowTitle>{t('planHistory.submitted')}</RowTitle>
            <RowValue>{submitted}</RowValue>
          </SectionRow>
          <SectionRow>
            <RowTitle>{t('planHistory.shipped')}</RowTitle>
            <RowValue>{shipped}</RowValue>
          </SectionRow>
        </>
      )}
      <SectionRow>
        <RowTitle>{t('planHistory.planned')}</RowTitle>
        <RowValue>{planned}</RowValue>
      </SectionRow>
      <Divider />
      {history.map((item) => (
        <SectionRow key={item.month}>
          <RowTitle>{item.month}</RowTitle>
          <RowValue>{item.shipped}</RowValue>
        </SectionRow>
      ))}
    </Wrapper>
  )
}

export default PlanHistory
